import React, { useEffect, useState } from "react";
import useDining from "../../services/Order/useDining";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { RotateCcw } from "lucide-react";
import LoadingScreen from "../../utils/LoadingScreen";

const QrCode = ({ userID }) => {
  const [loading, setLoading] = useState(false);
  const { tables, fetchTables } = useDining(userID);

  const loadTables = async () => {
    try {
      setLoading(true);
      await fetchTables();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, []);

  /* ---------------------- Styled QR (Card Design) ---------------------- */

  const generateStyledQR = async (tableNo, whatsappNumber, message, restaurantName) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message,
    )}`;

    const qr = await QRCode.toDataURL(link, {
      errorCorrectionLevel: "H",
      margin: 1,
      scale: 8,
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 500;
    canvas.height = 750;

    ctx.fillStyle = "#F6C23E";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#F8E6C1";
    ctx.fillRect(30, 30, 440, 690);

    const qrImg = new Image();
    qrImg.src = qr;

    return new Promise((resolve) => {
      qrImg.onload = () => {
        ctx.fillStyle = "#D9480F";
        ctx.font = "bold 32px Arial";
        ctx.textAlign = "center";
        ctx.fillText(restaurantName, canvas.width / 2, 120);

        ctx.drawImage(qrImg, 100, 160, 300, 300);

        ctx.fillStyle = "#333";
        ctx.font = "22px Arial";
        ctx.fillText("Scan to Order On WhatsApp", canvas.width / 2, 500);

        ctx.fillStyle = "#E8590C";
        ctx.fillRect(120, 540, 260, 60);

        ctx.fillStyle = "#fff";
        ctx.font = "bold 28px Arial";
        ctx.fillText(`TABLE ${tableNo}`, canvas.width / 2, 580);

        ctx.fillStyle = "#555";
        ctx.font = "18px Arial";
        ctx.fillText("Powered by", canvas.width / 2, 650);

        ctx.fillStyle = "#1c1f4a";
        ctx.font = "bold 28px Arial";
        ctx.fillText("chaafo", canvas.width / 2, 690);

        resolve(canvas.toDataURL("image/png"));
      };
    });
  };

  /* ---------------------- QR Only ---------------------- */

  const generatePlainQR = async (whatsappNumber, message) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message,
    )}`;

    return await QRCode.toDataURL(link, {
      errorCorrectionLevel: "H",
      margin: 1,
      scale: 20,
    });
  };

  /* ---------------------- Single Downloads ---------------------- */

  const handleDownloadQRCode = async (table) => {
    const img = await generateStyledQR(
      table.tableNo,
      table.whatsappNumber,
      table.message,
      table.retaurentName,
    );

    const a = document.createElement("a");
    a.href = img;
    a.download = `table-${table.tableNo}-10x15.png`;
    a.click();
  };

  const handleDownloadQRCode_5_10 = async (table) => {
    const img = await generateStyledQR_5_10(
      table.tableNo,
      table.whatsappNumber,
      table.message,
      table.retaurentName,
    );

    const a = document.createElement("a");
    a.href = img;
    a.download = `table-${table.tableNo}-5x10.png`;
    a.click();
  };

  const handleDownloadQRCode_only = async (table) => {
    const qr = await generatePlainQR(table.whatsappNumber, table.message);

    const a = document.createElement("a");
    a.href = qr;
    a.download = `table-${table.tableNo}-qr.png`;
    a.click();
  };

  /* ---------------------- ZIP Download ---------------------- */

  const handleDownloadAllQRCodesAsZip_only_QR = async () => {
    const zip = new JSZip();

    const images = await Promise.all(
      tables.map(async (table) => ({
        tableNo: table.tableNo,
        qr: await generatePlainQR(table.whatsappNumber, table.message),
      })),
    );

    images.forEach((img) => {
      const base64 = img.qr.split(",")[1];
      zip.file(`Table-${img.tableNo}.png`, base64, { base64: true });
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "Dining_QR_Codes.zip");
  };

  /* ---------------------- PDF Download ---------------------- */

  const handleDownloadAllQRCodesPDF_10_15 = async () => {
    const pdf = new jsPDF("portrait", "mm", "a4");

    const cardWidth = 90; // QR card width
    const cardHeight = 130; // QR card height

    const pageWidth = 210;
    const marginX = (pageWidth - cardWidth * 2) / 3;

    let x = marginX;
    let y = 10;

    let col = 0;

    for (let i = 0; i < tables.length; i++) {
      const table = tables[i];

      const img = await generateStyledQR(
        table.tableNo,
        table.whatsappNumber,
        table.message,
      );

      pdf.addImage(img, "PNG", x, y, cardWidth, cardHeight);

      col++;

      if (col === 2) {
        col = 0;
        x = marginX;
        y += cardHeight + 10;
      } else {
        x += cardWidth + marginX;
      }

      if (y + cardHeight > 290) {
        pdf.addPage();
        x = marginX;
        y = 10;
        col = 0;
      }
    }

    pdf.save("Dining_QR.pdf");
  };

  const generateStyledQR_5_10 = async (tableNo, whatsappNumber, message, restaurantName) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    const qr = await QRCode.toDataURL(link, {
      errorCorrectionLevel: "H",
      margin: 1,
      scale: 8,
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 600;
    canvas.height = 300;

    // Background
    ctx.fillStyle = "#F6C23E";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Inner card
    ctx.fillStyle = "#F8E6C1";
    ctx.fillRect(20, 20, 560, 260);

    const qrImg = new Image();
    qrImg.src = qr;

    return new Promise((resolve) => {
      qrImg.onload = () => {
        // QR
        ctx.drawImage(qrImg, 40, 50, 180, 180);

        // Title
        ctx.fillStyle = "#D9480F";
        ctx.font = "bold 26px Arial";
        ctx.textAlign = "left";
        ctx.fillText(restaurantName, 260, 80);

        // Text
        ctx.fillStyle = "#333";
        ctx.font = "18px Arial";
        ctx.fillText("Scan to Order", 260, 120);
        ctx.fillText("On WhatsApp", 260, 145);

        ctx.fillStyle = "#555";
        ctx.font = "14px Arial";
        ctx.textAlign = "left";
        ctx.fillText("Powered by", 260, 240);

        ctx.fillStyle = "#1c1f4a";
        ctx.font = "bold 18px Arial";
        ctx.fillText("chaafo", 260, 260);

        // Table box
        ctx.fillStyle = "#E8590C";
        ctx.fillRect(260, 170, 260, 45);

        ctx.fillStyle = "#fff";
        ctx.font = "bold 22px Arial";
        ctx.textAlign = "center";
        ctx.fillText(`TABLE ${tableNo}`, 390, 200);

        // Footer

        resolve(canvas.toDataURL("image/png"));
      };
    });
  };

  const handleDownloadAllQRCodesAsZip_10_15 = async () => {
    const zip = new JSZip();

    const images = await Promise.all(
      tables.map(async (table) => ({
        tableNo: table.tableNo,
        qr: await generateStyledQR(
          table.tableNo,
          table.whatsappNumber,
          table.message,
        ),
      })),
    );

    images.forEach((img) => {
      const base64 = img.qr.split(",")[1];
      zip.file(`Table-${img.tableNo}.png`, base64, { base64: true });
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "Dining_QR_Cards.zip");
  };

  const handleDownloadAllQRCodesPDF_5_10 = async () => {
    const pdf = new jsPDF("portrait", "mm", "a4");

    const qrWidth = 100; // 10cm
    const qrHeight = 50; // 5cm

    const pageWidth = 210;
    const pageHeight = 297;

    const cols = 2;
    const rows = 5;

    const marginX = (pageWidth - cols * qrWidth) / (cols + 1);
    const marginY = (pageHeight - rows * qrHeight) / (rows + 1);

    let index = 0;

    for (let i = 0; i < tables.length; i++) {
      const table = tables[i];

      const img = await generateStyledQR_5_10(
        table.tableNo,
        table.whatsappNumber,
        table.message,
      );

      const col = index % cols;
      const row = Math.floor(index / cols) % rows;

      const x = marginX + col * (qrWidth + marginX);
      const y = marginY + row * (qrHeight + marginY);

      pdf.addImage(img, "PNG", x, y, qrWidth, qrHeight);

      index++;

      if (index % (cols * rows) === 0 && i !== tables.length - 1) {
        pdf.addPage();
      }
    }

    pdf.save("Dining_QR_5x10.pdf");
  };

  const handleDownloadAllQRCodesAsZip_5_10 = async () => {
    const zip = new JSZip();

    const images = await Promise.all(
      tables.map(async (table) => ({
        tableNo: table.tableNo,
        qr: await generateStyledQR_5_10(
          table.tableNo,
          table.whatsappNumber,
          table.message,
        ),
      })),
    );

    images.forEach((img) => {
      const base64 = img.qr.split(",")[1];
      zip.file(`Table-${img.tableNo}-5x10.png`, base64, { base64: true });
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "Dining_QR_5x10.zip");
  };

  /* ---------------------- UI ---------------------- */

  return (
    <>
    
    <div className="p-6 min-h-screen montserrat-font-1">

      <div className="flex justify-between items-center mb-6 montserrat-font-1">
        <h1 className="text-2xl font-semibold">Download QR Codes</h1>

        <button
          onClick={loadTables}
          className="flex items-center gap-2 px-3 py-2 border rounded-md text-white bg-green-500"
        >
          <RotateCcw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <h2 className="text-lg font-semibold mb-4">Bulk Download Options</h2>

      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {/* 10x15 */}
        <div className="bg-white border border-blue-400 rounded-lg p-5 shadow-sm">
          <h3 className="text-blue-600 font-semibold mb-2">QR 10×15 cm</h3>
          <p className="text-gray-600 text-sm mb-4">
            {" "}
            Ideal for QR code standees. Includes restaurant name, table number,
            QR code, and WhatsApp prompt—perfect for displaying on counters,
            entrances, or near tables with ample space.{" "}
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleDownloadAllQRCodesPDF_10_15}
              className="bg-blue-600 text-white px-4 py-1 rounded text-sm"
            >
              Generate PDF
            </button>

            <button
              onClick={handleDownloadAllQRCodesAsZip_10_15}
              className="border border-blue-600 text-blue-600 px-4 py-1 rounded text-sm"
            >
              Download ZIP
            </button>
          </div>
        </div>

        {/* 5x10 */}
        <div className="bg-white border border-green-400 rounded-lg p-5 shadow-sm">
          <h3 className="text-green-600 font-semibold mb-2">QR 5×10 cm</h3>
          <p className="text-gray-600 text-sm mb-4">
            Perfect for table stickers in compact spaces. Shows the table
            number, QR code, and essential info—ideal for pasting directly on
            table surfaces, trays, or counters in smaller areas.
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleDownloadAllQRCodesPDF_5_10}
              className="bg-green-600 text-white px-4 py-1 rounded text-sm"
            >
              Generate PDF
            </button>

            <button
              onClick={handleDownloadAllQRCodesAsZip_5_10}
              className="border border-green-600 text-green-600 px-4 py-1 rounded text-sm"
            >
              Download ZIP
            </button>
          </div>
        </div>

        {/* QR Only */}
        <div className="bg-white border border-gray-400 rounded-lg p-5 shadow-sm montserrat-font-1">
          <h3 className="font-semibold mb-2">Only QR Code</h3>
          <p className="text-gray-600 text-sm mb-4 montserrat-font-2">
            A minimalist version featuring only the QR code. Designed for
            flexible use, such as embedding in menus, posters, or
            packaging—great for custom layouts and branding options.
          </p>
          <button
            onClick={handleDownloadAllQRCodesAsZip_only_QR}
            className="bg-gray-800 text-white px-4 py-1 rounded text-sm"
          >
            Download ZIP
          </button>
        </div>
      </div>

      {/* Table Section */}

      <h2 className="text-lg font-semibold montserrat-font-1">Table-wise QR Downloads</h2>
      <p className="text-sm text-gray-500 mb-5 montserrat-font-2">
        {" "}
        Choose your preferred format for each table below.{" "}
      </p>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingScreen text="Loading..." />
        </div>
      ) : (
        <div className="grid md:grid-cols-4 gap-4">
          {tables.map((table) => (
            <div
              key={table.id}
              className="bg-white border rounded-lg p-4 shadow-sm"
            >
              <h4 className="font-medium mb-3">
                Table <span className="font-semibold">#{table.tableNo}</span>
              </h4>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleDownloadQRCode(table)}
                  className="border border-blue-600 text-blue-600 px-3 py-1 rounded text-xs"
                >
                  QR 10×15
                </button>

                <button
                  onClick={() => handleDownloadQRCode_5_10(table)}
                  className="border border-green-600 text-green-600 px-3 py-1 rounded text-xs"
                >
                  QR 5×10
                </button>

                <button
                  onClick={() => handleDownloadQRCode_only(table)}
                  className="border border-gray-700 text-gray-700 px-3 py-1 rounded text-xs"
                >
                  QR Only
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
};

export default QrCode;
